import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Table, Button, Form, Row, Col, Badge, Spinner, Alert } from "react-bootstrap";
import { AiOutlineSearch, AiOutlinePlus, AiOutlineArrowUp, AiOutlineArrowDown } from "react-icons/ai";
import DriverSelector from "../../components/driverSelector";
import {
  DriverInvoiceViewModal,
  DriverInvoiceCheckModal,
} from "../../components/DriverInvoiceModals";

const statusVariant = (status) => {
  switch (status) {
    case "uploaded":
      return "secondary";
    case "approved":
      return "primary";
    case "paid":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "light";
  }
};

/** Клас підсвітки рядка таблиці за статусом (paid = зелений, approved = синій) */
const tableRowVariant = (status) => {
  switch (status) {
    case "uploaded":
      return "secondary";
    case "approved":
      return "primary";
    case "paid":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "";
  }
};

const sourceLabel = (source) => {
  if (source === "ksef") return "KSeF";
  if (source === "admin") return "Admin";
  return "Driver";
};

const getDefaultDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const first = `${year}-${month}-01`;
  const today = now.toISOString().slice(0, 10);
  return { from_date: first, to_date: today };
};

const DriverInvoicesPage = () => {
  const defaultRange = getDefaultDateRange();
  const [filters, setFilters] = useState({
    driver_id: "",
    status: "",
    source: "",
    from_date: defaultRange.from_date,
    to_date: defaultRange.to_date,
  });
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("uploaded_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchInvoices = async (pageToLoad = 1, sortByOverride = null, sortOrderOverride = null) => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page: pageToLoad,
        limit,
        sort_by: sortByOverride ?? sortBy,
        sort_order: sortOrderOverride ?? sortOrder,
      };
      if (filters.driver_id) params.driver_id = filters.driver_id;
      if (filters.status) params.status = filters.status;
      if (filters.source) params.source = filters.source;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;

      const token = localStorage.getItem("token");
      const res = await axios.get("/api/driver-invoices", {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.data?.success) {
        setInvoices(res.data.invoices || []);
        setTotal(res.data.total || 0);
        setPage(res.data.page || pageToLoad);
      } else {
        setError(res.data?.error || "Не вдалося завантажити фактури");
      }
    } catch (err) {
      console.error("Error loading driver invoices:", err);
      setError(err.response?.data?.error || err.message || "Помилка завантаження фактур");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (field) => {
    const nextOrder = sortBy === field && sortOrder === "desc" ? "asc" : "desc";
    setSortBy(field);
    setSortOrder(nextOrder);
    fetchInvoices(1, field, nextOrder);
  };

  const SortHeader = ({ field, label }) => (
    <th
      style={{ cursor: "pointer", userSelect: "none" }}
      onClick={() => handleSort(field)}
      title={`Сортувати по ${label}`}
    >
      {label}
      {sortBy === field && (
        <span className="ms-1">
          {sortOrder === "asc" ? <AiOutlineArrowUp size={14} /> : <AiOutlineArrowDown size={14} />}
        </span>
      )}
    </th>
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    fetchInvoices(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleRowClick = (invoice) => {
    setSelectedInvoice(invoice);
    if (invoice.status === "uploaded") {
      setIsCheckModalOpen(true);
      setIsViewModalOpen(false);
    } else {
      setIsViewModalOpen(true);
      setIsCheckModalOpen(false);
    }
  };

  const handleApproveInvoice = async (payload) => {
    if (!selectedInvoice?.id) return;
    try {
      setError("");
      const token = localStorage.getItem("token");
      await axios.put(`/api/driver-invoices/${selectedInvoice.id}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setIsCheckModalOpen(false);
      setSelectedInvoice(null);
      fetchInvoices(page);
    } catch (err) {
      console.error("Error approving invoice:", err);
      setError(err.response?.data?.error || err.message || "Помилка збереження");
    }
  };

  const handleRejectInvoice = async () => {
    if (!selectedInvoice?.id) return;
    try {
      setError("");
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/driver-invoices/${selectedInvoice.id}`,
        { status: "rejected" },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setIsCheckModalOpen(false);
      setSelectedInvoice(null);
      fetchInvoices(page);
    } catch (err) {
      console.error("Error rejecting invoice:", err);
      setError(err.response?.data?.error || err.message || "Помилка збереження");
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!id) return;
    try {
      setError("");
      const token = localStorage.getItem("token");
      await axios.delete(`/api/driver-invoices/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setIsCheckModalOpen(false);
      setSelectedInvoice(null);
      fetchInvoices(page);
    } catch (err) {
      console.error("Error deleting invoice:", err);
      setError(err.response?.data?.error || err.message || "Помилка видалення");
    }
  };

  const handleSaveViewInvoice = async (payload, onDone) => {
    if (!selectedInvoice?.id) return;
    try {
      setError("");
      const token = localStorage.getItem("token");
      await axios.put(`/api/driver-invoices/${selectedInvoice.id}`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setIsViewModalOpen(false);
      setSelectedInvoice(null);
      fetchInvoices(page);
    } catch (err) {
      console.error("Error saving invoice:", err);
      setError(err.response?.data?.error || err.message || "Помилка збереження");
    } finally {
      if (onDone) onDone();
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-0">
        <h2 className="mb-0">Фактури водіїв</h2>
        <Link to="/admin/driver-invoices/new">
          <Button variant="primary" size="sm" aria-label="Додати фактуру">
            <AiOutlinePlus size={20} />
          </Button>
        </Link>
      </div>

      <div className="mt-3 mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          <Col md={3}>
            <Form.Label>Водій</Form.Label>
            <DriverSelector
              selectedDriver={selectedDriver}
              setSelectedDriver={(option) => {
                setSelectedDriver(option);
                handleFilterChange("driver_id", option ? option.value : "");
              }}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Статус</Form.Label>
            <Form.Select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">Всі</option>
              <option value="uploaded">Завантажена</option>
              <option value="approved">Затверджена</option>
              <option value="paid">Виплачена</option>
              <option value="rejected">Відхилена</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Джерело</Form.Label>
            <Form.Select
              value={filters.source}
              onChange={(e) => handleFilterChange("source", e.target.value)}
            >
              <option value="">Всі</option>
              <option value="driver">Водій</option>
              <option value="admin">Адміністратор</option>
              <option value="ksef">KSeF</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Від дати</Form.Label>
            <Form.Control
              type="date"
              value={filters.from_date || ""}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>До дати</Form.Label>
            <Form.Control
              type="date"
              value={filters.to_date || ""}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
            />
          </Col>
          <Col md={1} className="d-flex align-items-end">
            <Button variant="primary" className="w-100" onClick={handleApplyFilters} disabled={loading} aria-label="Фільтрувати">
              {loading ? <Spinner animation="border" size="sm" /> : <AiOutlineSearch size={20} />}
            </Button>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>ID</th>
              <SortHeader field="driver" label="Водій" />
              <th>Джерело</th>
              <SortHeader field="status" label="Статус" />
              <th>Брутто</th>
              <th>VAT</th>
              <th>Повернення</th>
              <th>Рапорт</th>
              <SortHeader field="uploaded_at" label="Завантажена" />
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="text-center">
                  Немає фактур за вибраними фільтрами
                </td>
              </tr>
            )}
            {invoices.map((inv) => (
              <tr
                key={inv.id}
                className={tableRowVariant(inv.status) ? `table-${tableRowVariant(inv.status)}` : undefined}
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(inv)}
              >
                <td>{inv.id}</td>
                <td>
                  {inv.driver_first_name} {inv.driver_last_name} (ID: {inv.driver_id})
                </td>
                <td>{sourceLabel(inv.source)}</td>
                <td>
                  <Badge bg={statusVariant(inv.status)}>{inv.status}</Badge>
                </td>
                <td>{Number(inv.gross_amount || 0).toFixed(2)}</td>
                <td>{inv.vat_amount != null ? Number(inv.vat_amount).toFixed(2) : "-"}</td>
                <td>{inv.refund_amount != null ? Number(inv.refund_amount).toFixed(2) : "-"}</td>
                <td>{inv.payout_report_id || "-"}</td>
                <td>{inv.uploaded_at ? new Date(inv.uploaded_at).toLocaleString() : "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <div>
          Всього: {total} | Сторінка {page} з {totalPages}
        </div>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="me-2"
            disabled={page <= 1 || loading}
            onClick={() => fetchInvoices(page - 1)}
          >
            Назад
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => fetchInvoices(page + 1)}
          >
            Вперед
          </Button>
        </div>
      </div>

      <DriverInvoiceViewModal
        show={isViewModalOpen}
        onHide={() => setIsViewModalOpen(false)}
        invoice={selectedInvoice}
        onSave={handleSaveViewInvoice}
      />
      <DriverInvoiceCheckModal
        show={isCheckModalOpen}
        onHide={() => setIsCheckModalOpen(false)}
        invoice={selectedInvoice}
        onApprove={handleApproveInvoice}
        onReject={handleRejectInvoice}
        onDelete={handleDeleteInvoice}
      />
    </div>
  );
};

export default DriverInvoicesPage;

